import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import TavernSnackbar from './TavernSnackbar';

const ToastContext = createContext(null);

export const TOAST_AUTO_HIDE_DURATIONS = Object.freeze({
  success: 3200,
  info: 3600,
  warning: 5200,
  error: 7000,
});

export const resolveToastAutoHideDuration = (severity = 'success', options = {}) => {
  if (options.persist) return null;
  if (Number.isFinite(options.autoHideDuration)) return options.autoHideDuration;

  return TOAST_AUTO_HIDE_DURATIONS[severity] || TOAST_AUTO_HIDE_DURATIONS.info;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const nextToastIdRef = useRef(0);

  const showToast = useCallback((message, severity = 'success', options = {}) => {
    if (!message) return;

    nextToastIdRef.current += 1;
    const id = `${Date.now()}-${nextToastIdRef.current}`;

    setToasts((current) =>
      [
        ...current,
        {
          id,
          action: options.action,
          message,
          severity,
          autoHideDuration: resolveToastAutoHideDuration(severity, options),
        },
      ].slice(-4)
    );
  }, []);

  const closeToast = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const value = useMemo(
    () => ({
      notify: showToast,
      success: (message, options) => showToast(message, 'success', options),
      error: (message, options) => showToast(message, 'error', options),
      warning: (message, options) => showToast(message, 'warning', options),
      info: (message, options) => showToast(message, 'info', options),
    }),
    [showToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.map((toast, index) => (
        <TavernSnackbar
          key={toast.id}
          open
          action={toast.action}
          message={toast.message || ''}
          severity={toast.severity || 'success'}
          autoHideDuration={toast.autoHideDuration}
          stackIndex={toasts.length - index - 1}
          onClose={() => closeToast(toast.id)}
        />
      ))}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used inside ToastProvider');
  }

  return context;
};
